/**
 * Implements a coin icons
 * Hides a component if icons could not be loaded
 */
import { memo, useEffect, useState } from 'react';
import { Network } from '@kattana-io/common';
import useIcon from './useIcon';
import { getCDNIcon } from '../../helpers/coinIcon';
import IconProvider from '../../helpers/IconProvider';
import WTRXIcon from './icons/WTRX.svg';
import USDIcon from './icons/USD.svg';
import GBPIcon from './icons/GBP.svg';
import EURIcon from './icons/EUR.svg';
import UAHIcon from './icons/UAH.svg';
import TRYIcon from './icons/TRY.svg';
import fallbackIcon from './icons/placeholder.svg';

interface IPairIconsProps {
  chain: string;
  pair: string;
  base_address: string;
  quote_address: string;
  base_slug?: string;
  quote_slug?: string;
  vertical?: boolean;
}

const unwrapCoinName = (name: string) => {
  switch (name) {
  case 'WBTC':
    return 'BTC';
  case 'WETH':
    return 'ETH';
  case 'WBNB':
    return 'BNB';
  case 'WAVAX':
    return 'AVAX';
  default:
    return name;
  }
};

const FIAT_MAPPING: any = {
  USD: USDIcon,
  CAD: USDIcon,
  GBP: GBPIcon,
  EUR: EURIcon,
  UAH: UAHIcon,
  TRY: TRYIcon,
  TRX: WTRXIcon,
};

const iconCssClass = 'tickers__panel__table__row__pair__icons';
/**
 * Memoized data provider
 */
const PairIcons = ({
  pair,
  base_address,
  quote_address,
  chain,
  vertical,
}: IPairIconsProps) => {
  const [baseError, setBaseError] = useState(false);
  const [quoteError, setQuoteError] = useState(false);

  const [first, second] = pair.split('/');

  let base = '';
  let quote = '';

  if (chain != '') {
    const network = Network.fromChain(chain);
    if (network) {
      base = IconProvider.createFromNetworkAndPair(network, base_address).url();
    } else {
      base = fallbackIcon;
    }
    if (network) {
      quote = IconProvider.createFromNetworkAndPair(network, quote_address).url();
    } else {
      quote = fallbackIcon;
    }
  } else {
    if (FIAT_MAPPING[first]) {
      base = FIAT_MAPPING[first];
    } else {
      base = getCDNIcon(unwrapCoinName(first));
    }

    if (FIAT_MAPPING[second]) {
      quote = FIAT_MAPPING[second];
    } else {
      quote = getCDNIcon(unwrapCoinName(second));
    }
  }

  useEffect(() => {
    setBaseError(false);
    setQuoteError(false);
  }, [base, quote]);

  const basePath = useIcon(base, baseError, !!base);
  const quotePath = useIcon(quote, quoteError, !!quote);

  const prefix =
    `${vertical ? `${iconCssClass}--vertical ` : ''}` + iconCssClass;

  return (
    <span
      className={
        !quotePath || !basePath ? `${prefix} ${prefix}--loading` : prefix
      }
    >
      {basePath && (
        <img
          className={`${prefix}__base`}
          alt={first}
          src={baseError ? fallbackIcon : basePath}
          onError={() => setBaseError(true)}
        />
      )}
      {quotePath && (
        <img
          className={`${prefix}__quote`}
          alt={second}
          src={quoteError ? fallbackIcon : quotePath}
          onError={() => setQuoteError(true)}
        />
      )}
    </span>
  );
};

export default memo(PairIcons);
